import type { EditorState, Transaction } from '@tiptap/pm/state';
import { insertPoint } from '@tiptap/pm/transform';
import type { Node as ProseMirrorNode, NodeType, Schema } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

import { SIGNATURE_NODE_NAME } from './signatureNode';
import { pairSignature, signaturePairTarget } from './signaturePairing';

/**
 * A private MIME type, for the same reason the field palette has one: a slot dragged as
 * `text/plain` would drop into any input on the page and arrive as meaningless text.
 */
export const SIGNATURE_DRAG_MIME = 'application/x-dealstack-signature-slot';

/** The slot a dragged palette row carries. */
export interface SignatureSlotPayload {
	signingRoleId: string;
	isWitness: boolean;
	signatureIndex: number;
}

export const serialiseSignaturePayload = (slot: SignatureSlotPayload): string =>
	JSON.stringify(slot);

/** Parsed narrowly, so a stray drop cannot be coerced into a slot that no role owns. */
export const parseSignaturePayload = (raw: string): SignatureSlotPayload | null => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (typeof parsed !== 'object' || parsed === null) {
		return null;
	}
	const record: Record<string, unknown> = { ...parsed };
	const { signingRoleId, isWitness, signatureIndex } = record;
	if (typeof signingRoleId !== 'string' || signingRoleId === '') {
		return null;
	}
	if (typeof isWitness !== 'boolean') {
		return null;
	}
	// Integer and non-negative, matching the flow schema — a fractional or negative index would
	// save as a slot `enumerateSlots` never produces and be rejected as "extra".
	if (
		typeof signatureIndex !== 'number' ||
		!Number.isInteger(signatureIndex) ||
		signatureIndex < 0
	) {
		return null;
	}
	return { signingRoleId, isWitness, signatureIndex };
};

/**
 * The position after the block containing `pos`, at the nearest depth that accepts `type`.
 *
 * `insertPoint` — ProseMirror's own answer to "where can this node go near here" — only walks
 * out of its parent when `pos` is at the very START or END of it. A drop in the MIDDLE of a
 * paragraph gets null, because inserting a block there would mean splitting the paragraph.
 *
 * Splitting is the wrong answer for a signature: it would cut a clause in half around the
 * stamp. So a mid-paragraph drop lands after that paragraph instead. Same walk `insertPoint`
 * does for its end-of-parent case, minus the bail that refuses a position with later siblings —
 * inserting between two paragraphs is exactly what is wanted here.
 */
const afterEnclosingBlock = (doc: ProseMirrorNode, pos: number, type: NodeType): number | null => {
	const $pos = doc.resolve(pos);
	// Only a position INSIDE a run of text, which is the case this exists for. A drop that
	// resolves to a structural gap — between two table rows, or between two cells — is not
	// "mid-paragraph", and walking out from there finds the position after the whole TABLE:
	// the author aims at a cell of a two-column signature block and the stamp lands below it,
	// reported as a success. `insertPoint` refuses those, and so does this.
	if (!$pos.parent.isTextblock) {
		return null;
	}
	for (let depth = $pos.depth - 1; depth >= 0; depth -= 1) {
		const index = $pos.indexAfter(depth);
		if ($pos.node(depth).canReplaceWith(index, index, type)) {
			return $pos.after(depth + 1);
		}
	}
	return null;
};

/**
 * A transaction inserting a signature anchor at or near `pos`, or null if one cannot go there.
 *
 * A signature is a BLOCK, so — unlike a field — it cannot go mid-paragraph. `insertPoint` is
 * tried first because it gives the tightest answer at a block boundary; a mid-paragraph drop
 * falls back to just after the enclosing block.
 *
 * The `docChanged` check is the load-bearing part, and not for the reason one might assume:
 * `Transform.insert` funnels into `replace`, which computes a step and only applies it if one
 * exists — so an insert at a structurally invalid position **silently does nothing** rather than
 * throwing. Returning that transaction anyway meant the caller reported success, the drop was
 * consumed, and the palette's dot stayed empty with no way to tell why.
 */
/**
 * Where an anchor can go at or near `pos`, or null when nowhere can take one.
 *
 * Takes the DOC rather than the state, because a relocate asks this question of a document it
 * has already deleted the anchor out of — asking the original would give a position that has
 * since moved.
 */
export const anchorInsertPoint = (
	doc: ProseMirrorNode,
	schema: Schema,
	pos: number,
	nodeName: string
): number | null => {
	const nodeType = schema.nodes[nodeName];
	if (nodeType === undefined) {
		return null;
	}
	return insertPoint(doc, pos, nodeType) ?? afterEnclosingBlock(doc, pos, nodeType);
};

export const signatureInsertPoint = (
	doc: ProseMirrorNode,
	schema: Schema,
	pos: number
): number | null => anchorInsertPoint(doc, schema, pos, SIGNATURE_NODE_NAME);

export const insertSignatureAt = (
	state: EditorState,
	pos: number,
	slot: SignatureSlotPayload
): Transaction | null => {
	const signatureType = state.schema.nodes[SIGNATURE_NODE_NAME];
	const target = signatureInsertPoint(state.doc, state.schema, pos);
	if (signatureType === undefined || target === null) {
		return null;
	}
	const transaction = state.tr.insert(target, signatureType.create(slot));
	return transaction.docChanged ? transaction : null;
};

/**
 * A transaction moving an anchor already in the document to wherever `pos` resolves to.
 *
 * The counterpart to `pairSignature` for the rest of the page: dropped ON another anchor the two
 * pair, dropped anywhere else the anchor simply moves there. Both are reached by the same
 * gesture, so both have to answer in the same shape — a transaction, or null for "cannot go
 * there", which the caller answers by leaving the anchor where it was.
 *
 * The source is deleted first and the target mapped through, so the order cannot be wrong when
 * the anchor is moving backwards through the document. A target inside the anchor's own range is
 * refused: that is a drag that ended where it started, and reporting it as a move would put an
 * undo step in the history for nothing.
 */
export const relocateAnchor = (
	state: EditorState,
	source: { from: number; to: number },
	pos: number,
	node: ProseMirrorNode
): Transaction | null => {
	if (pos >= source.from && pos <= source.to) {
		return null;
	}
	const transaction = state.tr;
	transaction.delete(source.from, source.to);
	const target = anchorInsertPoint(
		transaction.doc,
		state.schema,
		transaction.mapping.map(pos),
		node.type.name
	);
	if (target === null) {
		return null;
	}
	transaction.insert(target, node);
	return transaction.docChanged ? transaction : null;
};

/**
 * Wires the two together. `posAtCoords` is the only part that needs a live view.
 *
 * A drop ONTO an existing anchor is a request to put the two side by side, and is tried first:
 * the alternative reading — insert a block here — is the one thing that cannot be what was
 * meant, since a block dropped on a stamp can only go above or below it.
 */
export const handleSignatureDrop = (view: EditorView, event: DragEvent): boolean => {
	const raw = event.dataTransfer?.getData(SIGNATURE_DRAG_MIME);
	if (raw === undefined || raw === '') {
		return false;
	}
	const slot = parseSignaturePayload(raw);
	if (slot === null) {
		return false;
	}
	const signatureType = view.state.schema.nodes[SIGNATURE_NODE_NAME];
	const target = signaturePairTarget(view, event);
	const paired =
		target === null || signatureType === undefined
			? null
			: pairSignature(view.state, target, signatureType.create(slot));
	if (paired !== null) {
		event.preventDefault();
		view.dispatch(paired);
		return true;
	}
	const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
	if (coords === null) {
		return false;
	}
	const transaction = insertSignatureAt(view.state, coords.pos, slot);
	if (transaction === null) {
		return false;
	}
	event.preventDefault();
	view.dispatch(transaction);
	return true;
};
